import withApplicationPropsV2 from '@/creditflowresearch/application/withApplicationPropsV2';
import getQueryParamsWithProductsRegions from '@/creditflowresearch/application/helpers/getQueryParamsWithProductsRegions';
import PageFiltersContextProvider from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { PageContentLayout } from '@/creditflowresearch/ui/common/components/ContentLayouts';
import StatsBase from '@/creditflowresearch/ui/components/creditFlow/Stats/Stats';
import { withStatsConfig } from '@/creditflowresearch/ui/components/creditFlow/Stats/withStatsConfig';
import {
  useAbsStatsConfig,
  useCorporateMarketStatsConfig,
  useEmergingMarketStatsConfig,
} from '@/creditflowresearch/ui/components/creditFlow/Stats/config/adapters';
import getServerSideStatsBreadcrumbs from '@/creditflowresearch/application/Breadcrumbs/serverSide/getServerSideStatsBreadcrumbs';
import getServerSideStats from '@/creditflowresearch/application/ArticlesAndStats/Stats/serverSide/getServerSideStats';
import getServerSideCategories
  from '@/creditflowresearch/application/ArticlesAndStats/Stats/serverSide/getServerSideCategories';
import { pageRedirectTo } from '@framework/router/Next/helpers';
import { getUrlWithAllCategory, isWrongCategoryId } from '@/creditflowresearch/application/ArticlesAndStats/Stats/helpers';
import { hasEmMarket } from '@/creditflowresearch/domain/Market/selectors/hasEmMarket';
import { Product } from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import { UsMarket, EmergingMarket } from '@/creditflowresearch/domain/Market';
import { EM_FILTER_REGION_IDS } from '@/creditflowresearch/application/PageFilters/constants';
import { IMarketCategoriesData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketCategory';
import { IMarketStatsData } from '@/creditflowresearch/domain/vo/ArticlesAndStats/Stats/IMarketStat';
import getDefaultQueryFilters from '@/creditflowresearch/application/helpers/getDefaultQueryFilters';
import { corporateValidators, emValidators } from '@/creditflowresearch/application/helpers/validators';
import type { QuerySanitizer } from '@/creditflowresearch/application/helpers/validators/types';
import { removeQueryParams } from '@framework/router';
import { useSearchParams } from '@framework/next/navigation';
import { ComponentType } from 'react';
import type { ParsedUrlQuery } from 'querystring';

const EM_DEFAULT_REGIONS = EM_FILTER_REGION_IDS
  .map((id) => Region.getAbbreviation(id))
  .filter(Boolean) as string[];

interface IDefaultsContext {
  defaultProducts: string[],
  defaultRegions: string[],
}

interface IStatsComponentProps {
  categoriesData: IMarketCategoriesData,
  statsData: IMarketStatsData,
}

interface IStatsMatcher {
  matches: (query: ParsedUrlQuery) => boolean,
  defaults: ((ctx: IDefaultsContext) => Record<string, string | string[]>) | null,
  validators?: Record<string, QuerySanitizer>,
  requiredParams: (ctx: IDefaultsContext) => Record<string, string | string[]>,
  StatsComponent: ComponentType<IStatsComponentProps>,
}

const STATS_MATCHERS: IStatsMatcher[] = [
  {
    matches: (query) => hasEmMarket(query),
    defaults: () => ({ markets: EmergingMarket.ABBREVIATION, regions: EM_DEFAULT_REGIONS.join(',') }),
    validators: emValidators,
    requiredParams: () => ({ markets: EmergingMarket.ABBREVIATION, regions: EM_DEFAULT_REGIONS }),
    StatsComponent: withStatsConfig(StatsBase, useEmergingMarketStatsConfig),
  },
  {
    matches: (query) => Boolean(query.markets),
    defaults: ({ defaultProducts }) => ({ products: defaultProducts.join(','), markets: UsMarket.ABBREVIATION }),
    validators: corporateValidators,
    requiredParams: ({ defaultProducts }) => ({ products: defaultProducts, markets: UsMarket.ABBREVIATION }),
    StatsComponent: withStatsConfig(StatsBase, useCorporateMarketStatsConfig),
  },
  {
    matches: () => true,
    defaults: null,
    requiredParams: ({ defaultProducts, defaultRegions }) => ({ products: defaultProducts, regions: defaultRegions }),
    StatsComponent: withStatsConfig(StatsBase, useAbsStatsConfig),
  },
];

export const getServerSideProps = withApplicationPropsV2(async (context) => {
  const { productIds = [], regionIds = [] } = context.productsRegions || {};
  const defaultProducts: string[] = productIds.map((id) => Product.getAbbreviation(id)).filter(Boolean) as string[];
  const defaultRegions: string[] = regionIds.map((id) => Region.getAbbreviation(id)).filter(Boolean) as string[];

  const matcher = STATS_MATCHERS.find(({ matches }) => matches(context.query)) as IStatsMatcher;

  if (matcher.defaults) {
    const sanitized = getDefaultQueryFilters({
      query: removeQueryParams(context.query, ['categoryId']),
      defaults: matcher.defaults({ defaultProducts, defaultRegions }),
      validators: matcher.validators,
    });

    if (sanitized) {
      return pageRedirectTo(sanitized);
    }
  }

  const queryParams = getQueryParamsWithProductsRegions(
    context.query,
    ['categoryId'],
    matcher.requiredParams({ defaultProducts, defaultRegions }),
  );

  if (queryParams) {
    return pageRedirectTo(queryParams);
  }

  const categoriesData = await getServerSideCategories(context);

  if (isWrongCategoryId(context, categoriesData.categories)) {
    return pageRedirectTo(getUrlWithAllCategory(context));
  }

  const statsData = await getServerSideStats(context);

  const breadcrumbs = getServerSideStatsBreadcrumbs(context.query);

  return {
    props: {
      categoriesData,
      statsData,
      breadcrumbs,
    },
  };
}, { needsLegacyDictionaries: false });

export default ({ categoriesData, statsData }: IStatsComponentProps) => {
  const searchParams = useSearchParams();
  const markets = searchParams.get('markets');
  const { StatsComponent } = STATS_MATCHERS.find(({ matches }) => matches({ markets })) as IStatsMatcher;

  return (
    <PageFiltersContextProvider>
      <PageContentLayout>
        <StatsComponent categoriesData={ categoriesData } statsData={ statsData } />
      </PageContentLayout>
    </PageFiltersContextProvider>
  );
};
